import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useUserData } from '@/hooks/useUserData'
import { WaitlistMap } from '@/components/waitlist/WaitlistMap'
import { WaitlistCityList } from '@/components/waitlist/WaitlistCityList'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Loader, Settings, Copy, Check } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogPortal,
} from '@/components/ui/dialog'
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cn } from "@/lib/utils"
import { toast } from 'sonner'

export interface WaitlistRequest {
  id: string
  userId: string
  artistInstagram: string
  location: {
    city: string
    country: string
    lat: number
    lon: number
  }
  isFlash: boolean
  customIdea?: string
  confidenceLevel: number
  createdAt: Date
}

export interface CityRequestGroup {
  city: string
  country: string
  lat: number
  lon: number
  requests: WaitlistRequest[]
  count: number
  region: string
}

// Helper function to determine region from country
function getRegionFromCountry(country: string): string {
  const europeanCountries = [
    'Albania', 'Andorra', 'Armenia', 'Austria', 'Azerbaijan', 'Belarus', 'Belgium',
    'Bosnia and Herzegovina', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic',
    'Denmark', 'Estonia', 'Finland', 'France', 'Georgia', 'Germany', 'Greece',
    'Hungary', 'Iceland', 'Ireland', 'Italy', 'Kosovo', 'Latvia', 'Liechtenstein',
    'Lithuania', 'Luxembourg', 'Malta', 'Moldova', 'Monaco', 'Montenegro',
    'Netherlands', 'North Macedonia', 'Norway', 'Poland', 'Portugal', 'Romania',
    'Russia', 'San Marino', 'Serbia', 'Slovakia', 'Slovenia', 'Spain', 'Sweden',
    'Switzerland', 'Turkey', 'Ukraine', 'United Kingdom', 'Vatican City'
  ]

  const asianCountries = [
    'Afghanistan', 'Bahrain', 'Bangladesh', 'Bhutan', 'Brunei', 'Cambodia', 'China',
    'India', 'Indonesia', 'Iran', 'Iraq', 'Israel', 'Japan', 'Jordan', 'Kazakhstan',
    'Kuwait', 'Kyrgyzstan', 'Laos', 'Lebanon', 'Malaysia', 'Maldives', 'Mongolia',
    'Myanmar', 'Nepal', 'North Korea', 'Oman', 'Pakistan', 'Palestine', 'Philippines',
    'Qatar', 'Saudi Arabia', 'Singapore', 'South Korea', 'Sri Lanka', 'Syria',
    'Taiwan', 'Tajikistan', 'Thailand', 'Timor-Leste', 'Turkmenistan', 'United Arab Emirates',
    'Uzbekistan', 'Vietnam', 'Yemen'
  ]

  const northAmericanCountries = [
    'Antigua and Barbuda', 'Bahamas', 'Barbados', 'Belize', 'Canada', 'Costa Rica',
    'Cuba', 'Dominica', 'Dominican Republic', 'El Salvador', 'Grenada', 'Guatemala',
    'Haiti', 'Honduras', 'Jamaica', 'Mexico', 'Nicaragua', 'Panama', 'Saint Kitts and Nevis',
    'Saint Lucia', 'Saint Vincent and the Grenadines', 'Trinidad and Tobago', 'United States'
  ]

  const southAmericanCountries = [
    'Argentina', 'Bolivia', 'Brazil', 'Chile', 'Colombia', 'Ecuador', 'Guyana',
    'Paraguay', 'Peru', 'Suriname', 'Uruguay', 'Venezuela'
  ]

  const africanCountries = [
    'Algeria', 'Angola', 'Benin', 'Botswana', 'Burkina Faso', 'Burundi', 'Cabo Verde',
    'Cameroon', 'Central African Republic', 'Chad', 'Comoros', 'Congo', 'Djibouti',
    'Egypt', 'Equatorial Guinea', 'Eritrea', 'Eswatini', 'Ethiopia', 'Gabon', 'Gambia',
    'Ghana', 'Guinea', 'Guinea-Bissau', 'Ivory Coast', 'Kenya', 'Lesotho', 'Liberia',
    'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius', 'Morocco',
    'Mozambique', 'Namibia', 'Niger', 'Nigeria', 'Rwanda', 'Sao Tome and Principe',
    'Senegal', 'Seychelles', 'Sierra Leone', 'Somalia', 'South Africa', 'South Sudan',
    'Sudan', 'Tanzania', 'Togo', 'Tunisia', 'Uganda', 'Zambia', 'Zimbabwe'
  ]

  const oceaniaCountries = [
    'Australia', 'Fiji', 'Kiribati', 'Marshall Islands', 'Micronesia', 'Nauru',
    'New Zealand', 'Palau', 'Papua New Guinea', 'Samoa', 'Solomon Islands', 'Tonga',
    'Tuvalu', 'Vanuatu'
  ]

  if (europeanCountries.includes(country)) return 'Europe'
  if (asianCountries.includes(country)) return 'Asia'
  if (northAmericanCountries.includes(country)) return 'North America'
  if (southAmericanCountries.includes(country)) return 'South America'
  if (africanCountries.includes(country)) return 'Africa'
  if (oceaniaCountries.includes(country)) return 'Oceania'
  
  return 'Other'
}

export default function ArtistWaitlistPage() {
  const navigate = useNavigate()
  const { userData, isArtist } = useUserData()
  const [requests, setRequests] = useState<WaitlistRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedRegion, setSelectedRegion] = useState<string>('all')
  const [showWaitlistSettings, setShowWaitlistSettings] = useState(false)
  const [waitlistLink, setWaitlistLink] = useState<string>('')
  const [isLinkCopied, setIsLinkCopied] = useState(false)

  // Fetch requests for this artist
  useEffect(() => {
    const loadWaitlistData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Check if user is an artist
        if (!isArtist || !userData?.artistId) {
          console.log('[ArtistWaitlistPage] Not an artist or no artistId:', { isArtist, artistId: userData?.artistId })
          setError('You need to be a registered artist to view your waitlist')
          setIsLoading(false)
          return
        }

        console.log('[ArtistWaitlistPage] Fetching artist doc with artistId:', userData.artistId)

        // Fetch the artist doc to get the displayName
        const artistDocRef = doc(db, 'artists', userData.artistId)
        const artistDocSnapshot = await getDoc(artistDocRef)

        if (!artistDocSnapshot.exists()) {
          console.log('[ArtistWaitlistPage] Artist doc not found for artistId:', userData.artistId)
          setError('Artist profile not found')
          setIsLoading(false)
          return
        }

        const artistDisplayName = artistDocSnapshot.data()?.displayName

        console.log('[ArtistWaitlistPage] Artist doc found. displayName:', artistDisplayName)

        if (!artistDisplayName) {
          console.log('[ArtistWaitlistPage] displayName field is missing from artist doc')
          setError('Artist displayName not found in profile')
          setIsLoading(false)
          return
        }

        // DEBUG: First, let's see ALL requests in the database
        console.log('[ArtistWaitlistPage] ========== DEBUG: ALL REQUESTS ==========')
        const allRequestsRef = collection(db, 'guestSpotRequests')
        const allRequestsSnapshot = await getDocs(allRequestsRef)
        console.log('[ArtistWaitlistPage] Total requests in database:', allRequestsSnapshot.size)
        allRequestsSnapshot.forEach((doc) => {
          const data = doc.data()
          console.log(`  - ID: ${doc.id}, artistInstagram: "${data.artistInstagram}", city: ${data.location?.city}`)
        })
        console.log('[ArtistWaitlistPage] ==========================================')
        
        // Query all requests for this artist by matching artistInstagram to the displayName
        console.log('[ArtistWaitlistPage] ========== QUERY START ==========')
        console.log('[ArtistWaitlistPage] Querying requests where artistInstagram ==', artistDisplayName)
        console.log('[ArtistWaitlistPage] artistDisplayName type:', typeof artistDisplayName)
        console.log('[ArtistWaitlistPage] artistDisplayName length:', artistDisplayName?.length)
        
        const requestsRef = collection(db, 'guestSpotRequests')
        const requestsQuery = query(
          requestsRef,
          where('artistInstagram', '==', artistDisplayName)
        )
        
        const snapshot = await getDocs(requestsQuery)
        
        console.log('[ArtistWaitlistPage] Found', snapshot.size, 'matching requests')
        console.log('[ArtistWaitlistPage] ========== DOCUMENTS FOUND ==========')
        
        const fetchedRequests: WaitlistRequest[] = []
        snapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data()
          console.log(`[ArtistWaitlistPage] Document ID: ${docSnapshot.id}`)
          console.log(`  - artistInstagram: "${data.artistInstagram}"`)
          console.log(`  - location: ${data.location?.city}, ${data.location?.country}`)
          console.log(`  - userId: ${data.userId}`)
          console.log(`  - createdAt: ${data.createdAt?.toDate()}`)
          
          fetchedRequests.push({
            id: docSnapshot.id,
            userId: data.userId,
            artistInstagram: data.artistInstagram,
            location: {
              city: data.location.city,
              country: data.location.country,
              lat: data.location.lat,
              lon: data.location.lon,
            },
            isFlash: data.isFlash,
            customIdea: data.customIdea,
            confidenceLevel: data.confidenceLevel,
            createdAt: data.createdAt?.toDate() || new Date(),
          })
        })
        
        console.log('[ArtistWaitlistPage] ========== QUERY END ==========')
        console.log('[ArtistWaitlistPage] Total requests loaded:', fetchedRequests.length)

        setRequests(fetchedRequests)
        setIsLoading(false)
      } catch (err) {
        console.error('Error loading waitlist data:', err)
        setError('Failed to load waitlist data')
        setIsLoading(false)
      }
    }

    loadWaitlistData()
  }, [isArtist, userData])

  // Group requests by city
  const cityGroups = useMemo(() => {
    const groups = new Map<string, CityRequestGroup>()

    requests.forEach((request) => {
      const cityKey = `${request.location.city},${request.location.country}`
      
      if (!groups.has(cityKey)) {
        groups.set(cityKey, {
          city: request.location.city,
          country: request.location.country,
          lat: request.location.lat,
          lon: request.location.lon,
          requests: [],
          count: 0,
          region: getRegionFromCountry(request.location.country),
        })
      }

      const group = groups.get(cityKey)!
      group.requests.push(request)
      group.count = group.requests.length
    })

    return Array.from(groups.values()).sort((a, b) => b.count - a.count)
  }, [requests])

  // Filter by region
  const filteredCityGroups = useMemo(() => {
    if (selectedRegion === 'all') {
      return cityGroups
    }
    return cityGroups.filter((group) => group.region === selectedRegion)
  }, [cityGroups, selectedRegion])

  // Get unique regions
  const regions = useMemo(() => {
    const uniqueRegions = new Set(cityGroups.map((group) => group.region))
    return Array.from(uniqueRegions).sort()
  }, [cityGroups])

  const handleBack = () => {
    navigate(-1)
  }

  const generateAndSetWaitlistLink = async () => {
    if (!userData?.artistId) return

    // Fetch artist displayName
    const artistDocRef = doc(db, 'artists', userData.artistId)
    const artistDocSnapshot = await getDoc(artistDocRef)
    const artistDisplayName = artistDocSnapshot.data()?.displayName

    if (!artistDisplayName) {
      toast.error('Unable to get artist name')
      return
    }

    const link = `getmoretats.com/waitlist/${artistDisplayName.toLowerCase()}`
    setWaitlistLink(link)
  }

  const handleCopyWaitlistLink = async () => {
    if (!waitlistLink) return
    
    try {
      await navigator.clipboard.writeText(waitlistLink)
      setIsLinkCopied(true)
      toast.success('Link copied!')
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setIsLinkCopied(false)
      }, 2000)
    } catch (error) {
      console.error('Failed to copy link:', error)
      toast.error('Failed to copy link')
    }
  }

  const handleOpenSettings = async () => {
    setShowWaitlistSettings(true)
    await generateAndSetWaitlistLink()
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen-mobile-minus-nav bg-background">
        <Loader className="w-8 h-8 animate-spin text-primary" />
        <p className="mt-4 text-muted-foreground">Loading your waitlist...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen-mobile-minus-nav bg-background phone-container-padded">
        <div className="text-center">
          <p className="text-lg font-semibold text-destructive mb-2">Error</p>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={handleBack} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col bg-background min-h-screen-mobile-minus-nav">
      {/* Content */}
      {requests.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 phone-container-padded text-center">
          <button
            onClick={handleBack}
            className="absolute top-4 left-4 p-2 hover:bg-muted rounded-md transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <p className="text-lg font-semibold mb-2">No requests yet</p>
          <p className="text-muted-foreground">
            When people request you as a guest artist, they'll appear here
          </p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Map - Fixed height at ~25vh */}
          <div className="flex-shrink-0 h-[25vh]">
            <div className="h-full w-full px-0 sm:px-[var(--min-side-padding)] mx-auto sm:max-w-[calc(var(--max-content-width-desktop)_+_(var(--min-side-padding)_*_2))]">
              <WaitlistMap cityGroups={filteredCityGroups} />
            </div>
          </div>

          {/* Header */}
          <div className="flex-shrink-0 sticky top-0 z-50 bg-background">
            <div className="phone-container-padded py-4">
              {/* Mobile layout - two rows */}
              <div className="md:hidden space-y-3">
                <div>
                  <h1 className="text-xl font-bold">Your Waitlist</h1>
                  <p className="text-sm text-muted-foreground">
                    {requests.length} {requests.length === 1 ? 'request' : 'requests'} from {cityGroups.length} {cityGroups.length === 1 ? 'city' : 'cities'}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-2">
                  {/* Settings Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleOpenSettings}
                    className="h-10 w-10 border border-border rounded-lg"
                  >
                    <Settings className="h-5 w-5" />
                  </Button>
                  
                  {/* Region Filter Dropdown */}
                  {regions.length > 0 && (
                    <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          All Regions
                        </SelectItem>
                        {regions.map((region) => {
                          return (
                            <SelectItem key={region} value={region}>
                              {region}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* Desktop layout - one row */}
              <div className="hidden md:flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleBack}
                    className="p-2 hover:bg-muted rounded-md transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div>
                    <h1 className="text-xl font-bold">Your Waitlist</h1>
                    <p className="text-sm text-muted-foreground">
                      {requests.length} {requests.length === 1 ? 'request' : 'requests'} from {cityGroups.length} {cityGroups.length === 1 ? 'city' : 'cities'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Settings Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleOpenSettings}
                    className="h-10 w-10 border border-border rounded-lg"
                  >
                    <Settings className="h-5 w-5" />
                  </Button>
                  
                  {/* Region Filter Dropdown */}
                  {regions.length > 0 && (
                    <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                      <SelectTrigger className="w-[175px]">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          All Regions
                        </SelectItem>
                        {regions.map((region) => {
                          return (
                            <SelectItem key={region} value={region}>
                              {region}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* List - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="phone-container-padded pt-4">
              {/* Promotional Box */}
              <div className="mb-6 p-4 bg-muted/50 border border-border rounded-lg">
                <h3 className="text-sm font-semibold mb-2">Share Your Waitlist</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Let your followers request you as a guest artist in their city. Share your personal waitlist link to get more location requests.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenSettings}
                  className="w-full sm:w-auto"
                >
                  Get Waitlist Link
                </Button>
              </div>
            </div>
            <WaitlistCityList cityGroups={filteredCityGroups} />
          </div>
        </div>
      )}

      {/* Waitlist Settings Dialog */}
      <Dialog open={showWaitlistSettings} onOpenChange={setShowWaitlistSettings}>
        <DialogPortal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-[60] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            )}
          />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-0 right-0 top-[50%] z-[60] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
              "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
            )}
          >
            {/* Header */}
            <div className="p-6 pb-4">
              <h2 className="text-xl font-semibold text-foreground text-center">
                Waitlist Settings
              </h2>
            </div>

            {/* Content */}
            <div className="px-6 pb-6 space-y-4">
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Personal Waitlist Link</h4>
                <p className="text-sm text-muted-foreground">
                  Share this link on your Instagram bio or other platforms
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {waitlistLink}
                  </span>
                  <button
                    onClick={handleCopyWaitlistLink}
                    className="p-1 hover:bg-muted rounded transition-colors flex-shrink-0"
                    title="Copy link"
                  >
                    {isLinkCopied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </div>
  )
}

